import React, { useState } from 'react';
import { UserPlus, X, Shield, Search, MoreHorizontal, Mail, Phone, Trash2, Edit2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { cn } from '@/utils/cn';
import type { TeamDetailData, TeamMember } from '../types/teamsDetail.types';
import { UpdateRoleModal } from './UpdateRoleModal';

interface MemberManagementBlockProps {
  members: TeamMember[];
  onAddMember: () => void;
  onRemoveMember: (id: string) => void;
  onUpdateRole: (id: string, roleId: string) => void;
  canUpdate?: boolean;
}

export function MemberManagementBlock({ members, onAddMember, onRemoveMember, onUpdateRole, canUpdate = false }: MemberManagementBlockProps) {
  const [search, setSearch] = useState('');
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);

  const filteredMembers = members.filter(m => 
    m.user.full_name.toLowerCase().includes(search.toLowerCase()) ||
    m.user.role_name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="bg-white rounded-md border border-slate-100 overflow-hidden">
      <div className="p-4 border-b border-slate-50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-emerald-50 text-emerald-600 flex items-center justify-center border border-emerald-100">
            <UserPlus className="w-5 h-5" />
          </div>
          <div>
            <h3 className="sm:text-sm text-xs font-normal text-slate-900 tracking-wider">Quản lý thành viên</h3>
            <p className="sm:text-sm text-xs text-slate-400">Thiết lập nhân sự và quyền hạn cho đội nhóm</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Tìm tên hoặc vai trò..."
              className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-100 rounded-md sm:text-sm text-xs outline-none focus:ring-1 focus:ring-emerald-500/20 focus:border-emerald-500/50 transition-all"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {canUpdate && (
            <Button onClick={onAddMember} className="bg-emerald-800 hover:bg-emerald-900 text-white sm:text-sm text-xs h-9 px-4 shrink-0 border-none">
              <UserPlus className="w-4 h-4 mr-2" />
              Thêm nhân sự
            </Button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50/50">
              <th className="px-6 py-3 sm:text-sm text-xs text-slate-500 font-normal">Thành viên</th>
              <th className="px-6 py-3 sm:text-sm text-xs text-slate-500 font-normal">Vai trò hệ thống</th>
              <th className="px-6 py-3 sm:text-sm text-xs text-slate-500 font-normal">Quyền hạn trong team</th>
              <th className="px-6 py-3 sm:text-sm text-xs text-slate-500 font-normal">Ngày tham gia</th>
              <th className="px-6 py-3 sm:text-sm text-xs text-slate-500 font-normal text-right">Thao tác</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {filteredMembers.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center text-slate-400 sm:text-sm text-xs italic">
                  Không tìm thấy thành viên phù hợp.
                </td>
              </tr>
            ) : (
              filteredMembers.map((member) => (
                <tr key={member.id} className="hover:bg-slate-50/30 transition-colors group">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-slate-100 border border-slate-200 overflow-hidden shrink-0">
                        {member.user.avatar_url ? (
                          <img src={member.user.avatar_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center sm:text-sm text-xs text-slate-400 font-normal">
                            {member.user.full_name[0]}
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="sm:text-sm text-xs text-slate-900 font-normal leading-tight">{member.user.full_name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="sm:text-sm text-xs text-slate-400 flex items-center gap-1">
                            <Mail className="w-2.5 h-2.5" /> {member.user.email || 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="sm:text-sm text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded-md font-normal">
                      {member.user.role_name}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="sm:text-sm text-xs font-normal text-slate-700">
                      {member.team_role_id === 'foreman' ? 'Tổ trưởng' :
                       member.team_role_id === 'vice_foreman' ? 'Tổ phó' : 'Thành viên'}
                    </span>
                  </td>
                  <td className="px-6 py-4 sm:text-sm text-xs text-slate-500">
                    {new Date(member.joined_at).toLocaleDateString('vi-VN')}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      {canUpdate && (
                        <>
                          <button 
                            onClick={() => setEditingMember(member)}
                            className="w-8 h-8 flex items-center justify-center rounded-md text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all"
                            title="Sửa quyền hạn"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => onRemoveMember(member.id)}
                            className="w-8 h-8 flex items-center justify-center rounded-md text-slate-400 hover:text-rose-500 hover:bg-rose-50 transition-all"
                            title="Xóa khỏi team"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      <button className="w-8 h-8 flex items-center justify-center rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-all">
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <UpdateRoleModal
        isOpen={!!editingMember}
        onClose={() => setEditingMember(null)}
        member={editingMember}
        onUpdate={(roleId) => {
          if (editingMember) {
            onUpdateRole(editingMember.id, roleId);
          }
        }}
      />
    </div>
  );
}
